import type { NavItem } from './rbac-types';

export const ADMIN_ONLY_PAGE_PREFIXES: string[] = [
  '/dashboard/approvals',
  '/dashboard/specops',
  '/dashboard/architektur',
  '/dashboard/cron-drops',
  '/dashboard/agent-jobs',
  '/dashboard/build-manager',
  '/dashboard/builder',
  '/dashboard/users',
  '/dashboard/design-tokens',
  '/dashboard/theme-changes',
  '/dashboard/claude',
  '/dashboard/agents',
  // Pages that render customer data, credentials or revenue straight from the
  // server. The API gate does not help here: these read the DB during SSR, or
  // fetch an internal route with the internal token, so nothing the reviewer's
  // browser does can be blocked after the fact. Verified leaks to a reviewer
  // session: /verbindungen served the TikTok client secret in the HTML,
  // /leads served lead email addresses in cleartext.
  '/dashboard/verbindungen',
  '/dashboard/leads',
  '/dashboard/lifecycle',
  '/dashboard/kommunikation',
  '/dashboard/kooperationen',
  '/dashboard/mail',
  '/dashboard/kita-mail',
  '/dashboard/shopify',
  '/dashboard/channels',
  // Renders the conversions table row by row, not aggregated: cid, uid, fbp,
  // user_agent and the click path per visitor. Pseudonymous, but personal data
  // under GDPR — this is not the aggregate analytics the neighbouring pages show.
  '/dashboard/events',
  // Not /dashboard/content — atoms, calendar, review and pipeline are exactly
  // what a reviewer is here for. Only this page selects raw_json wholesale,
  // which carries billing address, phone and email.
  '/dashboard/content/stories',
];

export const ADMIN_ONLY_API_PREFIXES: string[] = [
  '/api/ceo/',
  '/api/specops/',
  '/api/theme-changes/',
  '/api/builder/',
  '/api/build-manager/',
  '/api/cron-drops/',
  '/api/agents/',
  '/api/users/',
  '/api/nav-templates',
  '/api/architecture/',
  '/api/auth/invalidate-sessions',
  '/api/claude/',
  // PII / outbound-comms subsystems — not for the content-reviewer role.
  // (Intentionally-public mail endpoints live in proxy PUBLIC_EXACT and are
  // matched there before this gate; internal-token callers bypass it entirely.)
  '/api/kita/',
  '/api/mail/',
  '/api/comms/',
];

type WriteRule = { method: string; path: string; endsWith?: string };

export const REVIEWER_WRITE_WHITELIST: WriteRule[] = [
  { method: 'POST',   path: '/api/content-notes' },
  { method: 'PATCH',  path: '/api/content-notes/' },
  { method: 'DELETE', path: '/api/content-notes/' },
  { method: 'POST',   path: '/api/blog/',                    endsWith: '/review-comments' },
  { method: 'PATCH',  path: '/api/blog/review-comments/' },
  { method: 'DELETE', path: '/api/blog/review-comments/' },
  { method: 'POST',   path: '/api/posts/',                  endsWith: '/approve' },
  { method: 'POST',   path: '/api/blog/shopify/freigeben' },
  { method: 'POST',   path: '/api/reel-production/jobs/',   endsWith: '/qa/approve' },
  { method: 'POST',   path: '/api/reel-production/jobs/',   endsWith: '/approve-handoff' },
];

// Segment-aware, wie isNavAllowed(): ein Präfix trifft sich selbst und jeden tieferen
// Pfad, aber NICHT eine Schwester mit demselben Wortanfang. Reines startsWith() hätte
// mit '/dashboard/marketing' beim nächsten Zuwachs auch '/dashboard/marketing-machine'
// mitgesperrt — und umgekehrt suggeriert es eine Verwandtschaft, die es nicht gibt
// ('/dashboard/agents' und '/dashboard/agenten' sind zwei verschiedene Seiten).
// Gegengeprüft 2026-08-10: kein heute existierender /dashboard/*-Ordner verliert dadurch
// seinen Schutz — jeder Eintrag der Liste ist ein eigener Ordner, keiner ist Wortanfang
// eines anderen, nicht gelisteten.
export function isAdminOnlyPage(pathname: string): boolean {
  return ADMIN_ONLY_PAGE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + '/'),
  );
}

export function isAdminOnlyApi(pathname: string): boolean {
  return ADMIN_ONLY_API_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

export function isReviewerAllowedWrite(method: string, pathname: string): boolean {
  return REVIEWER_WRITE_WHITELIST.some((rule) => {
    if (rule.method !== method) return false;
    if (rule.endsWith) {
      return pathname.startsWith(rule.path) && pathname.endsWith(rule.endsWith);
    }
    if (rule.path.endsWith('/')) {
      return pathname.startsWith(rule.path);
    }
    return pathname === rule.path;
  });
}

export function getNavItemsForRole(role: string, items: NavItem[]): NavItem[] {
  if (role === 'admin') return items;
  return items.filter((item) => !isAdminOnlyPage(item.href));
}

// ── Nav-Templates: per-user page restriction (visibility + real gate) ────────
// `allowed` = href prefixes from the user's nav template, or null for no template
// (full access within role). A template can only RESTRICT, never escalate — the
// role gate (isAdminOnlyPage / isAdminOnlyApi) still applies independently.
// Matching is segment-aware: a prefix matches itself and any deeper sub-path,
// so allowing '/dashboard/blog' also allows '/dashboard/blog/123/review'.
// Pages every signed-in user reaches regardless of their template. The account
// page only exposes the user's own name/email/role and their own password
// change, so allowing it can never escalate. Without this a templated user gets
// bounced off their own account page — the NavBar renders that link outside the
// template filter, so it stays visible and simply fails when clicked.
const TEMPLATE_EXEMPT_PAGES: string[] = ['/dashboard/account'];

export function isNavAllowed(pathname: string, allowed: string[] | null): boolean {
  if (allowed === null) return true;
  if (TEMPLATE_EXEMPT_PAGES.some((p) => pathname === p || pathname.startsWith(p + '/'))) return true;
  return allowed.some((prefix) => pathname === prefix || pathname.startsWith(prefix + '/'));
}

export function navFallbackTarget(allowed: string[] | null): string {
  if (allowed && allowed.length > 0) return allowed[0];
  return '/dashboard';
}

// Restrict a NavItem list to a nav template (used client-side via kaju_nav cookie).
export function getNavItemsForTemplate(items: NavItem[], allowed: string[] | null): NavItem[] {
  if (allowed === null) return items;
  return items.filter((item) => isNavAllowed(item.href, allowed));
}
